import CESAppInfo from "../CESAppInfo";
import { ValueElement } from "./Base";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default class RecaptchaV3Element extends ValueElement {
  private static singleton: RecaptchaV3Element;

  private instance: HTMLDivElement;
  private scriptLoadPromise: Promise<void> | null = null;
  private loadedSiteKey: string | null = null;

  private constructor(name: string) {
    super(name);
    this.instance = document.createElement("div");
  }

  private loadScript(): Promise<void> {
    const siteKey = CESAppInfo.reCaptchakey;

    if (!siteKey) {
      return Promise.reject(new Error("V3 reCAPTCHA site key not configured"));
    }

    if (this.scriptLoadPromise && this.loadedSiteKey === siteKey) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise((resolve, reject) => {
      if (window.grecaptcha?.execute) {
        this.loadedSiteKey = siteKey;
        window.grecaptcha.ready(resolve);
        return;
      }

      const dashIdx = CESAppInfo.locale.indexOf("-");
      const hl = CESAppInfo.locale.substring(0, dashIdx > 0 ? dashIdx : CESAppInfo.locale.length);
      const recaptchaScriptId = "recaptchaScriptV3";

      // A leftover script tag whose load/error events already fired would
      // never notify new listeners — always replace it so retries actually
      // reload the script instead of waiting on events that can't happen.
      document.getElementById(recaptchaScriptId)?.remove();

      const script = document.createElement("script");

      let timeoutId: number | null = null;
      const fail = (error: Error) => {
        if (timeoutId) clearTimeout(timeoutId);
        script.remove();
        this.scriptLoadPromise = null;
        reject(error);
      };
      const handleLoad = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (!window.grecaptcha) {
          fail(new Error("V3 reCAPTCHA failed to load"));
          return;
        }
        this.loadedSiteKey = siteKey;
        window.grecaptcha.ready(resolve);
      };

      script.id = recaptchaScriptId;
      script.setAttribute("data-sitekey", siteKey);
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}&hl=${hl}`;
      script.async = true;
      script.defer = true;
      script.addEventListener("load", handleLoad, { once: true });
      script.addEventListener(
        "error",
        () => fail(new Error("Failed to load V3 reCAPTCHA script")),
        { once: true }
      );
      document.querySelector("head")!.appendChild(script);

      timeoutId = window.setTimeout(
        () => fail(new Error("V3 reCAPTCHA script load timeout")),
        10000
      );
    });

    return this.scriptLoadPromise;
  }

  public static getInstance(name: string = "recaptcha"): RecaptchaV3Element {
    if (!RecaptchaV3Element.singleton || RecaptchaV3Element.singleton.name !== name) {
      RecaptchaV3Element.singleton = new RecaptchaV3Element(name);
    }

    RecaptchaV3Element.singleton.loadScript().catch((error) => {
      console.error("Failed to load V3 reCAPTCHA script:", error);
    });

    return RecaptchaV3Element.singleton;
  }

  get element(): HTMLElement {
    return this.instance;
  }

  get value(): any {
    return this.submit();
  }

  set value(_val: any) {}

  private async executeOnce(operation?: string): Promise<string> {
    await this.loadScript();
    await new Promise<void>((resolve) => window.grecaptcha.ready(resolve));
    return window.grecaptcha.execute(CESAppInfo.reCaptchakey, { action: operation || "submit" });
  }

  // Resolves with "" (never rejects) so awaiting the `value` getter in
  // form.getValues() cannot blow up an unrelated form submission.
  submit = async (operation?: string): Promise<string> => {
    try {
      return await this.executeOnce(operation);
    } catch (firstError) {
      console.error("V3 reCAPTCHA failed, retrying with a fresh script:", firstError);
      // Force a full reload — a stale or broken script is the most common
      // cause, and loadScript replaces the tag once the cached promise is gone.
      this.scriptLoadPromise = null;
      try {
        return await this.executeOnce(operation);
      } catch (retryError) {
        console.error("V3 reCAPTCHA error (after retry):", retryError);
        return "";
      }
    }
  }

  resetTokenValue = () => {}

  reset = () => {}

  isApplied = () => false

  isVerified = () => false

  dispose(): void { }

  disable(): void { }
}
